import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store'

// Only eagerly load what's needed immediately
import Login from '../views/Login.vue'

// Lazy load everything else
const Register = () => import('../views/Register.vue')
const ForgotPassword = () => import('../views/ForgotPassword.vue')
const RestorePassword = () => import('../views/SetPassword.vue')
const ContactUs = () => import('../views/ContactUs.vue')
const ContactUsSuccess = () => import('../views/ContactUsSuccess.vue')
const Dashboard = () => import('../views/Dashboard.vue')
const AccountPicker = () => import('../views/AccountPicker.vue')

const ProfileLayout = () => import('../views/profile/Dashboard.vue')

const Communications = () => import('../views/communications/Dashboard.vue')
const Phone = () => import('../views/communications/Phone.vue')
const Messages = () => import('../views/communications/Messages.vue')
const Contacts = () => import('../views/communications/Contacts.vue')
const Team = () => import('../views/communications/Team.vue')
const Meet = () => import('../views/communications/Meet.vue')
const MeetingRoom = () => import('../views/communications/MeetingRoom.vue')
const Chat = () => import('../views/communications/Chat.vue')

const PBXDashboard = () => import('../views/pbx/Dashboard.vue')
const AutoAttendant = () => import('../views/pbx/AutoAttendant.vue')
const CallForward = () => import('../views/pbx/CallForward.vue')
const CallGroups = () => import('../views/pbx/CallGroups.vue')
const Extensions = () => import('../views/pbx/Extensions.vue')
const HoldMusic = () => import('../views/pbx/HoldMusic.vue')
const Numbers = () => import('../views/pbx/Numbers.vue')
const PhoneLines = () => import('../views/pbx/PhoneLines.vue')
const Schedule = () => import('../views/pbx/Schedule.vue')
const Users = () => import('../views/pbx/Users.vue')
const VoiceMail = () => import('../views/pbx/VoiceMail.vue')
const RingGroups = () => import('../views/pbx/RingGroups.vue')
const PbxUserProfile = () => import('../views/pbx/UserProfile.vue')

const MonitoringDashboard = () => import('../views/monitoring/Dashboard.vue')
const CallDetailRecords = () => import('../views/monitoring/CallDetailRecords.vue')
const CallGraphs = () => import('../views/monitoring/CallGraphs.vue')
const WLB = () => import('../views/monitoring/WLB.vue')
const ActivityLog = () => import('../views/monitoring/ActivityLog.vue')

const PublicCallGraphs = () => import('../views/monitoring/PublicCallGraphs.vue')

const SalesDashboard = () => import('../views/sales/Dashboard.vue')
const Leads = () => import('../views/sales/leads.vue')
const Customers = () => import('../views/sales/customers.vue')

const GuestJoin = () => import('../views/GuestJoin.vue')

const routes = [
  // Public graph link (no login required)
  {
    path: '/public/call-graphs/:profileId/:token',
    name: 'PublicCallGraphs',
    component: PublicCallGraphs,
    meta: { requiresAuth: false }
  },

  // Public guest meeting join (no login required)
  {
    path: '/meet/j/:code',
    name: 'GuestJoin',
    component: GuestJoin,
    meta: { requiresAuth: false },
    props: true
  },

  {
    path: '/',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true }
  },
  {
    path: '/account-picker',
    name: 'AccountPicker',
    component: AccountPicker,
    meta: { requiresAuth: true }
  },

  // Guest-only pages
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: { requiresAuth: false, guestOnly: true }
  },
  {
    path: '/register',
    name: 'register',
    component: Register,
    meta: { requiresAuth: false, guestOnly: true }
  },
  {
    path: '/forgot',
    name: 'forgot',
    component: ForgotPassword,
    meta: { requiresAuth: false, guestOnly: true }
  },
  {
    path: '/password-reset',
    name: 'setPassword',
    component: RestorePassword,
    meta: { requiresAuth: false, guestOnly: true }
  },

  // Public pages
  {
    path: '/contactUs',
    name: 'contactUs',
    component: ContactUs,
    meta: { requiresAuth: false }
  },
  {
    path: '/contactUs-success',
    name: 'contactUsSuccess',
    component: ContactUsSuccess,
    meta: { requiresAuth: false }
  },

  // Profile
  {
    path: '/profile',
    name: 'profile',
    component: ProfileLayout,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/settings',
    redirect: '/profile'
  },

  // Communications
  {
    path: '/communications',
    name: 'communications',
    component: Communications,
    meta: { requiresAuth: true },
    children: [
      { path: 'phone', name: 'Phone', component: Phone },
      { path: 'messages', name: 'Messages', component: Messages },
      { path: 'contacts', name: 'Contacts', component: Contacts },
      { path: 'team', name: 'Team', component: Team },
      { path: 'meet', name: 'Meet', component: Meet },
      { path: 'meet/room/:id', name: 'MeetingRoom', component: MeetingRoom, props: true },
      { path: 'chat', name: 'Chat', component: Chat }
    ]
  },

  // PBX
  {
    path: '/pbx',
    name: 'pbx',
    component: PBXDashboard,
    meta: { requiresAuth: true },
    children: [
      { path: 'auto-attendant', name: 'AutoAttendant', component: AutoAttendant },
      { path: 'call-forward', name: 'CallForward', component: CallForward },
      { path: 'call-groups', name: 'CallGroups', component: CallGroups },
      { path: 'ring-groups', name: 'RingGroups', component: RingGroups },
      { path: 'extensions', name: 'Extensions', component: Extensions },
      { path: 'hold-music', name: 'HoldMusic', component: HoldMusic },
      { path: 'numbers', name: 'Numbers', component: Numbers },
      { path: 'phone-lines', name: 'PhoneLines', component: PhoneLines },
      { path: 'schedule', name: 'Schedule', component: Schedule },
      { path: 'users', name: 'Users', component: Users },
      { path: 'users/:id', name: 'PbxUserProfile', component: PbxUserProfile, props: true },
      { path: 'voice-mail', name: 'VoiceMail', component: VoiceMail }
    ]
  },

  // Monitoring
  {
    path: '/monitoring',
    name: 'monitoring',
    component: MonitoringDashboard,
    meta: { requiresAuth: true },
    children: [
      { path: 'call-detail-records', name: 'CallDetailRecords', component: CallDetailRecords },
      { path: 'call-graphs', name: 'CallGraphs', component: CallGraphs },
      { path: 'wlb', name: 'WLB', component: WLB },
      { path: 'activity-log', name: 'ActivityLog', component: ActivityLog }
    ]
  },

  // Sales
  {
    path: '/sales',
    name: 'sales',
    component: SalesDashboard,
    meta: { requiresAuth: true },
    children: [
      { path: 'leads', name: 'Leads', component: Leads },
      { path: 'customers', name: 'Customers', component: Customers }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

function redirectToRoleRoute(roles) {
  if (roles.includes('Communications')) return { name: 'communications' }
  if (roles.includes('PBX Admin')) return { name: 'pbx' }
  if (roles.includes('Sales Agent')) return { name: 'sales' }
  return { name: 'Dashboard' }
}

router.beforeEach(async (to, from, next) => {
  if (to.query.impersonate) {
    localStorage.setItem('token', to.query.impersonate)
    window.location.href = '/'
    return
  }

  const loggedIn = store.getters['auth/isAuthenticated'] || !!localStorage.getItem('token')

  if (loggedIn) {
    if (to.meta.guestOnly) return next('/')

    if (!store.state.customerRoles.roles.length) {
      try {
        await store.dispatch('customerRoles/fetchCustomerRoles')
      } catch (e) {
        console.error('Failed to load roles before routing:', e)
      }
    }

    if (to.path === '/') {
      const roles = store.state.customerRoles.roles
      if (roles && roles.length) return next(redirectToRoleRoute(roles))
    }

    return next()
  }

  if (to.meta.requiresAuth) return next({ path: '/login' })
  return next()
})

export default router
